// Dónde dibujar un popover que se portalea al `body` — ADR-2026-09-13-el-popover-de-lanzamiento-se-portalea.
//
// Un popover `absolute` dentro de su botón lo recorta el primer ancestro que scrollea
// (`overflow-y: auto` también recorta en HORIZONTAL: un eje no-visible convierte al otro
// en un scrollport). Las cards de la pantalla de generadas y las de la columna de
// resultados viven justamente adentro de uno, así que el panel de "Enviar a lanzamiento"
// salía cortado por la mitad. La salida es portalearlo al body con `position: fixed`, y
// entonces hay que hacer a mano la cuenta que hacía el navegador.
//
// Es un módulo puro —sin DOM, sin React— porque es lo único de esto que se puede testear
// en este repo (los tests no renderizan componentes).

/** La caja del botón que dispara el popover, en coordenadas de viewport (`getBoundingClientRect`). */
export interface CajaAncla {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PosicionPopover {
  left: number;
  /** Uno de los dos es `null`: el panel se ancla de un solo lado. */
  top: number | null;
  bottom: number | null;
  /** Hueco disponible de ese lado. El panel lo usa de `maxHeight` y scrollea adentro. */
  maxAlto: number;
}

export interface OpcionesPopover {
  direccion: 'arriba' | 'abajo';
  alineacion: 'izquierda' | 'derecha';
  /** Ancho del panel. Hace falta para alinearlo por su borde derecho. */
  ancho: number;
  viewport: { ancho: number; alto: number };
  /** Aire entre el botón y el panel. */
  separacion?: number;
  /** Aire mínimo contra los bordes de la pantalla. */
  margen?: number;
  /** Menos que esto de un lado es "no entra": si del otro hay más, se da vuelta. */
  altoMinimo?: number;
}

export function posicionDePopover(
  ancla: CajaAncla,
  {
    direccion,
    alineacion,
    ancho,
    viewport,
    separacion = 8,
    margen = 8,
    altoMinimo = 180,
  }: OpcionesPopover,
): PosicionPopover {
  // Horizontal: se alinea por el borde que pidió el host y después se lo empuja adentro
  // de la pantalla. El `Math.max` va último para que en un viewport más angosto que el
  // panel gane el margen izquierdo (si no, el clamp de la derecha devolvería negativo).
  const deseado = alineacion === 'derecha' ? ancla.right - ancho : ancla.left;
  const left = Math.max(margen, Math.min(deseado, viewport.ancho - ancho - margen));

  // Vertical: el alto del panel no se conoce hasta dibujarlo, así que hacia arriba se
  // ancla por `bottom` (contra el techo del botón) y hacia abajo por `top`.
  const huecoArriba = ancla.top - separacion - margen;
  const huecoAbajo = viewport.alto - ancla.bottom - separacion - margen;
  // Se da vuelta sólo si del lado pedido no entra y del otro sí: si no entra de ninguno
  // se respeta lo que pidió el host y el panel scrollea adentro.
  const hueco = direccion === 'arriba' ? huecoArriba : huecoAbajo;
  const huecoOpuesto = direccion === 'arriba' ? huecoAbajo : huecoArriba;
  const daVuelta = hueco < altoMinimo && huecoOpuesto > hueco;
  const lado = daVuelta ? (direccion === 'arriba' ? 'abajo' : 'arriba') : direccion;

  return lado === 'arriba'
    ? {
        left,
        top: null,
        bottom: viewport.alto - ancla.top + separacion,
        maxAlto: Math.max(0, huecoArriba),
      }
    : {
        left,
        top: ancla.bottom + separacion,
        bottom: null,
        maxAlto: Math.max(0, huecoAbajo),
      };
}

export default posicionDePopover;
